import {
    IconBrandLinkedin,
    IconBuildingStore,
    IconCalendar,
    IconCamera,
    IconLink,
    IconMail,
    IconMapPin,
    IconMessage,
    IconMusic,
    IconPhone,
    IconShoppingCart,
    IconUser,
    IconVideo,
    IconWifi,
    IconWorld,
} from '@tabler/icons-react';
import { BRAND_MARKS } from './qrBrandMarks';

/**
 * Built-in centre logos.
 *
 * Each preset is a rounded tile (brand colour) with a white glyph knocked out —
 * the same element is used for the picker thumbnail and, serialised to an SVG
 * data URL, for the logo the renderer embeds.
 *
 * Social presets use each brand's real mark and official colour (see
 * ./qrBrandMarks, generated from Simple Icons); the generic ones use Tabler line
 * icons (MIT). Brand names and marks remain the property of their owners.
 */
export const LOGO_PRESETS = [
    { id: 'wifi', Icon: IconWifi, color: '#0F4C81' },
    { id: 'phone', Icon: IconPhone, color: '#1E9E5E' },
    { id: 'mail', Icon: IconMail, color: '#D33A2C' },
    { id: 'location', Icon: IconMapPin, color: '#E4572E' },
    { id: 'link', Icon: IconLink, color: '#3D3833' },
    { id: 'contact', Icon: IconUser, color: '#6D28D9' },
    { id: 'cart', Icon: IconShoppingCart, color: '#B45309' },
    { id: 'store', Icon: IconBuildingStore, color: '#166534' },
    { id: 'calendar', Icon: IconCalendar, color: '#0F766E' },
    { id: 'camera', Icon: IconCamera, color: '#9D174D' },
    { id: 'music', Icon: IconMusic, color: '#7C3AED' },
    { id: 'video', Icon: IconVideo, color: '#B91C1C' },
    { id: 'message', Icon: IconMessage, color: '#2563EB' },
    { id: 'web', Icon: IconWorld, color: '#0891B2' },
    { id: 'facebook', mark: BRAND_MARKS.facebook.path, color: BRAND_MARKS.facebook.hex },
    { id: 'messenger', mark: BRAND_MARKS.messenger.path, color: BRAND_MARKS.messenger.hex },
    { id: 'whatsapp', mark: BRAND_MARKS.whatsapp.path, color: BRAND_MARKS.whatsapp.hex },
    { id: 'instagram', mark: BRAND_MARKS.instagram.path, color: BRAND_MARKS.instagram.hex },
    { id: 'youtube', mark: BRAND_MARKS.youtube.path, color: BRAND_MARKS.youtube.hex },
    { id: 'x', mark: BRAND_MARKS.x.path, color: BRAND_MARKS.x.hex },
    { id: 'linkedin', Icon: IconBrandLinkedin, color: '#0A66C2' },
    { id: 'telegram', mark: BRAND_MARKS.telegram.path, color: BRAND_MARKS.telegram.hex },
    { id: 'tiktok', mark: BRAND_MARKS.tiktok.path, color: BRAND_MARKS.tiktok.hex },
    { id: 'snapchat', mark: BRAND_MARKS.snapchat.path, color: BRAND_MARKS.snapchat.hex },
    { id: 'pinterest', mark: BRAND_MARKS.pinterest.path, color: BRAND_MARKS.pinterest.hex },
    { id: 'spotify', mark: BRAND_MARKS.spotify.path, color: BRAND_MARKS.spotify.hex },
    { id: 'google', mark: BRAND_MARKS.google.path, color: BRAND_MARKS.google.hex },
    { id: 'gmail', mark: BRAND_MARKS.gmail.path, color: BRAND_MARKS.gmail.hex },
    { id: 'github', mark: BRAND_MARKS.github.path, color: BRAND_MARKS.github.hex },
    { id: 'apple', mark: BRAND_MARKS.apple.path, color: BRAND_MARKS.apple.hex },
];

export const LOGO_PRESET_IDS = LOGO_PRESETS.map((preset) => preset.id);

/**
 * The logo a content type suggests. Picking "WiFi" or "YouTube" and then hunting
 * for the same icon in the logo grid is busywork, so the type picks it — but only
 * while the logo is still untouched (see `logoAutoRef` in QrCodeImage): a logo the
 * user chose, uploaded, or deliberately cleared is never overwritten.
 *
 * Types with no obvious mark (`text`, `crypto`) are absent on purpose — a wrong
 * logo reads worse than none.
 */
const TYPE_LOGOS = {
    url: 'link',
    email: 'mail',
    phone: 'phone',
    sms: 'message',
    wifi: 'wifi',
    vcard: 'contact',
    mecard: 'contact',
    location: 'location',
    event: 'calendar',
    payment: 'cart',
    applink: 'web',
    whatsapp: 'whatsapp',
    facebook: 'facebook',
    instagram: 'instagram',
    twitter: 'x',
    youtube: 'youtube',
    linkedin: 'linkedin',
    telegram: 'telegram',
};

/** Preset id suggested for a content type, or null when none fits. */
export function logoIdForContentType(type) {
    return TYPE_LOGOS[type] || null;
}

export function findLogoPreset(id) {
    return LOGO_PRESETS.find((preset) => preset.id === id) || null;
}

/**
 * One preset tile. A nested `<svg>` (the Tabler glyph) sits inside the coloured
 * plate, so the whole tile scales as a single image. Colours fall back to the
 * preset's own; `plate: false` drops the backdrop and draws the glyph alone in
 * the plate colour.
 *
 * `radius` rounds the plate. The picker keeps its soft corners, but the tile fed
 * to the QR renderer passes 0 — otherwise a rounded plate is baked into the
 * artwork and the logo corner-radius slider can never square it off.
 */
export function LogoPresetTile({ preset, size = 42, bg, icon = '#ffffff', plate = true, radius = 14 }) {
    const { Icon, mark } = preset;
    const plateColor = bg || preset.color;
    const glyphColor = plate ? icon : plateColor;
    // Brand marks are solid 24×24 paths and need more breathing room than the
    // line glyphs, which are drawn edge-to-edge.
    const inset = plate ? (mark ? 15 : 13) : (mark ? 7 : 4);
    const span = 64 - inset * 2;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 64 64"
            aria-hidden="true"
        >
            {plate ? <rect width="64" height="64" rx={radius} fill={plateColor} /> : null}
            {mark ? (
                <svg x={inset} y={inset} width={span} height={span} viewBox="0 0 24 24">
                    <path d={mark} fill={glyphColor} />
                </svg>
            ) : (
                <Icon x={inset} y={inset} width={span} height={span} color={glyphColor} stroke={2.1} />
            )}
        </svg>
    );
}

/**
 * Serialise an already-rendered tile `<svg>` node to a data URL the QR renderer
 * can embed. The node comes from a hidden `<LogoPresetTile>` in the tree, so no
 * second React root (and no `flushSync`) is involved.
 */
export function tileNodeToDataUrl(node) {
    if (!node) return null;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(node))}`;
}
